import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import ApiService from '../services/api';

interface Settings {
  enabled: boolean;
  hour_utc: number;
  last_run_at: string | null;
  last_status: string | null;
  last_error: string | null;
  last_size_bytes: number | null;
}

function formatLocalTimestamp(isoString: string): string {
  const d = new Date(isoString);
  const tzAbbrev = new Intl.DateTimeFormat('en-AU', { timeZoneName: 'short' })
    .formatToParts(d)
    .find((p) => p.type === 'timeZoneName')?.value || '';
  // date-fns formats using the browser's local timezone, so this is already local time —
  // the trailing abbreviation just needs to match, not say "UTC" (the storage format).
  return `${format(d, 'dd MMM yyyy, h:mm a')} ${tzAbbrev}`.trim();
}

function hourOptionLabel(hourUtc: number): string {
  const aest = (hourUtc + 10) % 24;
  const aedt = (hourUtc + 11) % 24;
  const pad = (h: number) => h.toString().padStart(2, '0');
  return `${pad(hourUtc)}:00 UTC  (${pad(aest)}:00 AEST / ${pad(aedt)}:00 AEDT)`;
}

const DbBackupSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    ApiService.adminGetDbBackupSettings()
      .then((data) => setSettings(data))
      .catch(() => setError('Failed to load backup settings.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.adminSaveDbBackupSettings({ enabled: settings.enabled, hour_utc: settings.hour_utc });
      setSuccess('Backup settings saved.');
      load();
    } catch {
      setError('Failed to save backup settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleBackupNow = async () => {
    setRunningNow(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await ApiService.adminTriggerDbBackup();
      const sizeMb = (result.size_bytes / (1024 * 1024)).toFixed(1);
      setSuccess(`Backup completed successfully (${sizeMb} MB).`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Backup failed.');
    } finally {
      load();
      setRunningNow(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return <Alert severity="error">Failed to load backup settings.</Alert>;
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant="h6" gutterBottom>
        Nightly Database Backup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Backs up the database every night and uploads it to Wasabi (siv-sonops-backups).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
          />
        }
        label="Nightly backup enabled"
      />

      <Box sx={{ mt: 2, mb: 3, maxWidth: 380 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Scheduled time
        </Typography>
        <Select
          fullWidth
          size="small"
          value={settings.hour_utc}
          onChange={(e) => setSettings({ ...settings, hour_utc: Number(e.target.value) })}
        >
          {Array.from({ length: 24 }, (_, h) => (
            <MenuItem key={h} value={h}>
              {hourOptionLabel(h)}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save Settings'}
      </Button>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" gutterBottom>
        Last run
      </Typography>
      {settings.last_run_at ? (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip
              label={settings.last_status === 'success' ? 'Success' : 'Failed'}
              color={settings.last_status === 'success' ? 'success' : 'error'}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {formatLocalTimestamp(settings.last_run_at)}
            </Typography>
          </Box>
          {settings.last_status === 'success' && settings.last_size_bytes && (
            <Typography variant="body2" color="text.secondary">
              Size: {(settings.last_size_bytes / (1024 * 1024)).toFixed(1)} MB
            </Typography>
          )}
          {settings.last_status === 'failed' && settings.last_error && (
            <Typography variant="body2" color="error">
              {settings.last_error}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No backup has run yet.
        </Typography>
      )}

      <Button
        variant="outlined"
        startIcon={runningNow ? <CircularProgress size={16} /> : <CloudUploadIcon />}
        onClick={handleBackupNow}
        disabled={runningNow}
      >
        {runningNow ? 'Backing up…' : 'Backup Now'}
      </Button>
    </Paper>
  );
};

export default DbBackupSettingsTab;
